/**
 * useSilentAudio
 *
 * Keeps the browser's audio session alive by maintaining a playing (but silent)
 * <audio> element whose source is a MediaStream from the AudioContext's output.
 *
 * WHY THIS IS NEEDED
 * ------------------
 * iOS and Android require an active <audio> or <video> element to:
 *   1. Show the OS lock-screen / notification-bar MediaSession card ("Spotify card")
 *   2. Keep the WebKit/Chrome audio session in the foreground category so the
 *      AudioContext is NOT suspended when the app is backgrounded or the screen
 *      is locked.
 *
 * Without this hook, two bugs occur on a real phone:
 *   - The MediaSession lock-screen card never appears (the OS dismisses metadata
 *     set on navigator.mediaSession because there is no active audio session).
 *   - Bells get cut off with a "dead bell" sound when the user switches apps or
 *     locks the screen, because iOS immediately suspends the AudioContext.
 *     AudioContextWatcher (MOB-01/02 fix) resumes it on return, which is why the
 *     user hears the remaining portion of the bell when they come back.
 *
 * HOW IT WORKS
 * ------------
 * createMediaStreamDestination() produces a MediaStreamAudioDestinationNode
 * whose .stream is a live MediaStream. Assigning that stream as the srcObject of
 * a playing <audio> element signals to the OS "this tab has an active audio
 * session". The stream carries no audible signal (nothing is connected to the
 * destination node), so there is no volume impact.
 *
 * This is a well-established pattern used by browser-based music players,
 * podcast apps, and metronomes that use Web Audio API.
 */
import { useEffect, useRef } from "react";
import { audioPlane } from "../planes";
import { TEST_IDS } from "../testing/testIds";

function tryCreateBridge(): HTMLAudioElement | null {
  const ctx = audioPlane.getAudioContext();
  if (!ctx) return null;
  try {
    const dest = ctx.createMediaStreamDestination();
    const el = document.createElement("audio");
    el.setAttribute("data-testid", TEST_IDS.SILENT_AUDIO);
    el.setAttribute("aria-hidden", "true");
    el.srcObject = dest.stream;
    // Must be in the DOM for OS to register an active audio session
    // and for document.querySelector to find it in E2E tests.
    document.body.appendChild(el);
    return el;
  } catch {
    return null;
  }
}

export function useSilentAudio(isRunning: boolean): void {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isRunning) {
      if (retryRef.current) {
        clearTimeout(retryRef.current);
        retryRef.current = null;
      }
      audioRef.current?.pause();
      return;
    }

    function startBridge(): void {
      if (!audioRef.current) {
        const el = tryCreateBridge();
        if (!el) {
          // AudioContext not yet initialised — retry after 200 ms
          retryRef.current = setTimeout(startBridge, 200);
          return;
        }
        audioRef.current = el;
      }
      audioRef.current.play().catch(() => {
        // play() can fail under strict autoplay policies; the AudioContext
        // wake-lock path (MOB-01/02) still recovers audio on foreground.
      });
    }

    startBridge();
  }, [isRunning]);

  useEffect(() => {
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.remove();
        audioRef.current = null;
      }
    };
  }, []);
}
